import { useEffect, useState } from 'react';
import { VisionneuseJustificatif } from './VisionneuseJustificatif';

/**
 * Aperçu d'un justificatif, en plein écran et DANS l'application.
 *
 * L'ouverture dans un nouvel onglet paraissait plus simple : sur téléphone,
 * elle ferme la porte derrière soi. Le lecteur du système s'affiche sans aucun
 * retour visible, et l'on se retrouve coincé devant son propre ticket.
 *
 * D'où un aperçu maison, avec une fermeture évidente : un bouton, la touche
 * Échap, et le bouton « précédent » du téléphone — qui doit refermer l'aperçu
 * plutôt que quitter la page.
 *
 * L'affichage lui-même est délégué à `VisionneuseJustificatif`, partagée avec
 * l'écran de comparaison : image ou PDF, ce sont les mêmes pièges.
 *
 * Le ZOOM par boutons existe parce que le pincement n'existe pas sur
 * ordinateur, et qu'un vrai justificatif — photographié de travers, froissé,
 * imprimé sur du papier thermique pâli — se lit rarement à sa taille d'origine.
 */
const ECHELLE_MIN = 0.5;
const ECHELLE_MAX = 4;

export function ApercuJustificatif({ url, onFermer }: { url: string; onFermer: () => void }) {
  const [echelle, setEchelle] = useState(1);
  const zoomer = (facteur: number) =>
    setEchelle((e) => Math.min(Math.max(Math.round(e * facteur * 100) / 100, ECHELLE_MIN), ECHELLE_MAX));
  // Échap ferme, comme partout ailleurs.
  useEffect(() => {
    const surTouche = (e: KeyboardEvent) => e.key === 'Escape' && onFermer();
    window.addEventListener('keydown', surTouche);
    return () => window.removeEventListener('keydown', surTouche);
  }, [onFermer]);

  // Le bouton « précédent » du téléphone referme l'aperçu au lieu de quitter
  // la dépense : c'est le geste que tout le monde tente en premier.
  useEffect(() => {
    window.history.pushState({ apercu: true }, '');
    const surRetour = () => onFermer();
    window.addEventListener('popstate', surRetour);
    return () => {
      window.removeEventListener('popstate', surRetour);
      if (window.history.state?.apercu) window.history.back();
    };
  }, [onFermer]);

  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-nuit/95">
      <header className="flex items-center justify-between gap-2 px-4 pb-2 pt-[max(0.75rem,env(safe-area-inset-top))]">
        <span className="font-titre text-sm font-bold text-white">Justificatif</span>

        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1 rounded-lg ring-1 ring-white/30">
            <button
              type="button"
              onClick={() => zoomer(1 / 1.25)}
              aria-label="Réduire"
              disabled={echelle <= ECHELLE_MIN}
              className="flex h-11 w-11 items-center justify-center text-lg text-white disabled:opacity-40"
            >
              −
            </button>
            {/* Le pourcentage remet à 100 % : après trois zooms, on ne sait
                plus où l'on en est et l'on cherche le bouton de retour. */}
            <button
              type="button"
              onClick={() => setEchelle(1)}
              className="min-w-14 text-sm text-white/80"
            >
              {Math.round(echelle * 100)} %
            </button>
            <button
              type="button"
              onClick={() => zoomer(1.25)}
              aria-label="Agrandir"
              disabled={echelle >= ECHELLE_MAX}
              className="flex h-11 w-11 items-center justify-center text-lg text-white disabled:opacity-40"
            >
              +
            </button>
          </div>

          <a
            href={url}
            target="_blank"
            rel="noreferrer"
            className="hidden min-h-11 items-center rounded-lg px-3 text-sm text-white ring-1 ring-white/30 sm:flex"
          >
            Ouvrir à part
          </a>
          {/* Une croix, pas seulement un mot : c'est le geste qu'on cherche en
              premier pour refermer une image ouverte en grand. */}
          <button
            type="button"
            onClick={onFermer}
            aria-label="Fermer l’aperçu"
            className="flex h-11 w-11 items-center justify-center rounded-lg bg-white text-xl font-semibold text-nuit"
          >
            ×
          </button>
        </div>
      </header>

      <div className="flex-1 overflow-auto px-2 pb-[max(0.5rem,env(safe-area-inset-bottom))]">
        {/* La largeur suit le zoom : au-delà de 100 %, la pièce déborde et se
            fait défiler, ce qui est exactement ce qu'on veut pour lire un
            montant illisible. */}
        <div className="mx-auto" style={{ maxWidth: `${Math.max(48, 48 * echelle)}rem` }}>
          <VisionneuseJustificatif url={url} sombre echelle={echelle} />
        </div>
      </div>
    </div>
  );
}
